import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cloneDeep, isEmpty } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectMaxNumItemsPerHTMLDownload } from '../../redux/appSettings';
import { selectControls } from '../../redux/controls';
import { selectSystemID } from '../../redux/queryPanel';
import { loadDownloadCancel, loadDownloadStart, selectDownloadProgress, selectDownloading, selectErrMsg, selectIdsForDownload } from '../../redux/queryResults';
import { DownloadRequest } from '../../redux/queryResults/models';
import { DOWNLOAD_FORM, DOWNLOAD_TYPES } from '../../redux/queryResults/models/queryResults.types';

import Modal from '../Modals/Modal';
import { WindowType } from '../Modals/models/Modal.types';
import NQTCheckBox from '../NQTCheckBox/NQTCheckBox';
import { CHECKBOX_GROUP_TYPES } from '../NQTCheckBox/NQTCheckBox.models';
import { NQTRadioButton } from '../NQTRadioButton/NQTRadioButton';
import Spinner from '../Spinner/Spinner';
import './DownloadPanel.scss';
import { DownloadFormatsDict, ITEM_LIST_COL_HEADER_LABELS, ITEM_LIST_DB_COLUMNS, TAB_TYPE, TEST_TYPE } from './models';
import { DownloadEnums, DownloadPanelProps } from './models/DownloadPanel.interfaces';
import { resetSelections, updateSelected, tabToShareTopFromDownload } from './models/DownloadPanel.utils';

import ga4 from '../../shared/analyticsEvents';

export const DownloadPanel = ({tabData}: DownloadPanelProps): JSX.Element => {
  const controls = useSelector(selectControls);
  const isDownloading = useSelector(selectDownloading);
  const downloadProgress = useSelector(selectDownloadProgress);
  const idsForDownload = useSelector(selectIdsForDownload);
  const errMsg = useSelector(selectErrMsg);
  const maxNumItemsPerHTMLDownload = useSelector(selectMaxNumItemsPerHTMLDownload);
  const systemID = useSelector(selectSystemID);
  const dispatch = useDispatch();

  const downloadPanelEl = useRef<HTMLDivElement>(null);
  const onFocusEl = useRef<HTMLButtonElement>(null);

  //tracks collapsed/expanded states
  const [accExpanded, setAccExpanded] = useState<boolean>(false);

  //checkbox fields state
  const [hideCheckboxes, setHideCheckboxes] = useState<boolean>(false);
  // const [disableExcelOptions, setDisableExcelOptions] = useState<boolean>(false);
  const [tabsForDownload, setTabsForDownload] = useState<DownloadEnums[]>([]);
  const [testsForDownload, setTestsForDownload] = useState<DownloadEnums[]>([]);
  const [excelOptsForDownload, setExcelOptsForDownload] = useState<DownloadEnums[]>([]);
  const [prevTabsForDownload, setPrevTabsForDownload] = useState<DownloadEnums[]>([]);
  const [prevTestsForDownload, setPrevTestsForDownload] = useState<DownloadEnums[]>([]);
  const [prevExcelOptsForDownload, setprevExcelOptsForDownload] = useState<DownloadEnums[]>([]);
  const [varExpanded, setVarExpanded] = useState<boolean>(true);

  //radio-button fields state
  const [formats, setFormats] = useState<DownloadEnums[]>([]);
  // const [downloadFormats, setDownloadFormats] = useState<DownloadEnums[]>([]);
  const [selectedFormat, setSelectedFormat] = useState<DOWNLOAD_FORM>(DOWNLOAD_FORM.HTML);
  const [prevSelectedFormat, setPrevSelectedFormat] = useState<DOWNLOAD_FORM>(DOWNLOAD_FORM.HTML);

  // variables to trap user focus to downloadPanel
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState('');
  const [modalType, setModalType] = useState<WindowType>('DESC');

  const [isValid, setIsValid] = useState<boolean>(true);
  const [focusOnSelectAll, setFocusOnSelectAll] = useState<boolean>(false);

  // Tracks whether the download in progress was initiated from this component instance,
  // so the progress indicator is only shown underneath the button that was clicked.
  const [localDownloading, setLocalDownloading] = useState<boolean>(false);


  const onPreview = useCallback(() => {
    if (isDownloading) {
      return;
    }
    if (isValid) {
      if ( tabData && idsForDownload && !isEmpty(idsForDownload)) {
        setPrevTabsForDownload(cloneDeep(tabsForDownload));
        setPrevTestsForDownload(cloneDeep(testsForDownload));
        setPrevSelectedFormat(cloneDeep(selectedFormat));

        // Add edge case for when FORM === EXCEL
        const isExcel = selectedFormat === DOWNLOAD_FORM.EXCEL;

        // Add edge case for when FORM === HTML, can't download over 200 items
        const itemIDList = tabData.filter(tb => !!idsForDownload[tb.itemTableID]).map(tb => tb.itemTableID);

        if (selectedFormat === DOWNLOAD_FORM.HTML && itemIDList.length > maxNumItemsPerHTMLDownload) {
          const maxDownloadDescription = controls?.MaxDownload.controlText;
          setModalContent(maxDownloadDescription ? maxDownloadDescription.replace(/\$\$MAXITEMS\$\$/, maxNumItemsPerHTMLDownload.toString()) : '');
          setModalType('DESC');
          setFocusOnSelectAll(true);
          setIsModalOpen(true);
        } else {
          const downloadPayload: DownloadRequest = {
            ItemListColHeaderLabels: systemID == '3' ?
              ITEM_LIST_COL_HEADER_LABELS.map(lb => (lb === 'Grade') ? 'Age' : lb) :
              ITEM_LIST_COL_HEADER_LABELS,
            ItemListDBColumns: systemID == '3' ?
              ITEM_LIST_DB_COLUMNS.map(lb => (lb == 'grade') ? 'age' : lb) :
              ITEM_LIST_DB_COLUMNS,
            ItemIDList: tabData.filter(tb => !!idsForDownload[tb.itemTableID]).map(tb => tb.itemTableID),
            DLoadType:
              isExcel ?
                [DOWNLOAD_TYPES.ITEMLIST].concat(excelOptsForDownload.filter(opt => opt.isSelected).map(opt => opt.value as DOWNLOAD_TYPES)) :
                [
                  ...tabsForDownload.filter(tab => tab.isSelected).map((tab) => tab.value as DOWNLOAD_TYPES),
                  ...testsForDownload.filter(test => test.isSelected).map(test => test.value as DOWNLOAD_TYPES)
                ]
            ,
            Downloads: selectedFormat,
            SystemID: systemID ? systemID : '1'
          };
          ga4.onGA4DownloadContent(selectedFormat);
          dispatch(
            loadDownloadStart(downloadPayload)
          );
          setLocalDownloading(true);
        }

      } else {
        // activate desc modal that directs redirects focus to SelectAll
        setModalContent(controls?.DwnldWarn ? controls?.DwnldWarn?.controlText : '');
        setModalType('DESC');
        setFocusOnSelectAll(true);
        setIsModalOpen(true);
      }
    }
  },
  [
    controls?.DwnldWarn,
    controls?.MaxDownload.controlText,
    dispatch,
    idsForDownload,
    isDownloading,
    isValid,
    maxNumItemsPerHTMLDownload,
    selectedFormat,
    systemID,
    tabData,
    tabsForDownload,
    testsForDownload,
    excelOptsForDownload
  ]);

  /**
   * Closes Modal and either fires onApply or resets focus to subject button
   * Important note: if event-listener is active, focus must be reset inside explorePanel
   * or event-listener will continuous open Modal
   */
  const onModalClose = useCallback((applyChanges?: boolean) => {
    setIsModalOpen(false);

    // If modalType === 'PROMPT, either apply changes or resetSelections
    // else reset focus back to queryPanel to make valid selections
    if (applyChanges) {
      onPreview();
    } else if (modalType === 'PROMPT') {
      resetSelections(
        prevTabsForDownload,
        prevTestsForDownload,
        prevSelectedFormat,
        prevExcelOptsForDownload,
        setTabsForDownload,
        setTestsForDownload,
        setSelectedFormat,
        setprevExcelOptsForDownload,
        setHideCheckboxes
      );
    } else {
      if (onFocusEl.current && !focusOnSelectAll) {
        onFocusEl.current.focus();
      }
    }
  },[focusOnSelectAll, modalType, onPreview, prevSelectedFormat, prevTabsForDownload, prevTestsForDownload, prevExcelOptsForDownload]);

  const updateSelectedFormat = useCallback((val: string) => {
    if (val && val in DownloadFormatsDict) {
      if (DownloadFormatsDict[val] !== selectedFormat) {
        if (DownloadFormatsDict[val] === DOWNLOAD_FORM.EXCEL) {
          //setHideCheckboxes(true);
          //setDisableExcelOptions(false);
        } else {
          //setHideCheckboxes(false);
          //setDisableExcelOptions(true);
          const newOpts = excelOptsForDownload.map(opt => ({ ...opt, isSelected: false }));
          setExcelOptsForDownload( newOpts);
        }
        setSelectedFormat(DownloadFormatsDict[val]);
      }
    }
  }, [selectedFormat, excelOptsForDownload]);

  // fires on mount, creates checkbox and radio objects
  useEffect(() => {
    if (controls) {
      const tabs = [
        {
          label: controls?.DwnldQ?.controlText,
          value: DOWNLOAD_TYPES.QUESTION,
          isSelected: true
        },
        {
          label: controls?.DwnldQD?.controlText,
          value: DOWNLOAD_TYPES.QUESTIONDESC,
          isSelected: true
        },
        {
          label: controls?.DwnldSG?.controlText,
          value: DOWNLOAD_TYPES.SCOREGUIDE_KEY,
          isSelected: true
        },
        {
          label: controls?.DwnldSR?.controlText,
          value: DOWNLOAD_TYPES.SAMPLERESPONSES,
          isSelected: true
        },
      ];
      const tests = [
        {
          label: controls?.DwnldWhatTest?.controlText,
          value: DOWNLOAD_TYPES.TESTONLY,
          isSelected: false
        },
        {
          label: controls?.DwnldWhatTestAndSG?.controlText,
          value: DOWNLOAD_TYPES.TESTWITHSCORING,
          isSelected: false
        }
      ];

      const formats = [
        {
          label: controls?.DwnldHTML?.controlText,
          value: DOWNLOAD_FORM.HTML,
          isSelected: true
        },
        {
          label: controls?.DwnldPDF?.controlText,
          value: DOWNLOAD_FORM.PDF,
          isSelected: false
        },
        {
          label: controls?.DwnldWord?.controlText,
          value: DOWNLOAD_FORM.WORD,
          isSelected: false
        },

      ];


      const excelOptions = [
        {
          label: controls?.DwnldQList?.controlText,
          value: DOWNLOAD_TYPES.PERFDATA,
          isSelected: false
        }
      ];

      setTabsForDownload(tabs);
      setPrevTabsForDownload(cloneDeep(tabs));
      setTestsForDownload(tests);
      setPrevTestsForDownload(cloneDeep(tests));
      setExcelOptsForDownload(excelOptions);
      setprevExcelOptsForDownload(cloneDeep(excelOptions));
      setFormats(formats);
      //setDownloadFormats(downloadFormats);

    }
  }, [controls]
  );

  // fires when selections are changed
  useEffect(() => {
    if (
      (
        tabsForDownload.some(tab => tab.isSelected) ||
        testsForDownload.some(test => test.isSelected) ||
        selectedFormat === DOWNLOAD_FORM.EXCEL
      )
    ) {
      setIsValid(true);
    } else {
      setIsValid(false);
    }
  }, [selectedFormat, tabsForDownload, testsForDownload]);

  // fires when focusOnSelectAll is toggled - redirects focus to ListView checkboxes if true
  useEffect(() => {
    if (focusOnSelectAll && !isModalOpen) {
      const selectAllElement = document.querySelector('#table-header--select-all + .nqt-checkbox') as HTMLElement;
      if (selectAllElement) {
        selectAllElement.focus();
      }
      setFocusOnSelectAll(false);
    }
  }, [focusOnSelectAll, isModalOpen]);

  useEffect(() => {
    if (errMsg) {
      setModalContent(typeof errMsg === 'string' ? errMsg : errMsg?.message ?? 'An error occurred.');
      setModalType('DESC');
      setFocusOnSelectAll(false);
      setIsModalOpen(true);
    }
  }, [errMsg]);

  // resets the local downloading flag once the download completes, fails, or is cancelled
  useEffect(() => {
    if (!isDownloading && localDownloading) {
      setLocalDownloading(false);
    }
  }, [isDownloading, localDownloading]);

  return (
    <section className="nqt-dp">
      <Modal
        bodyText={modalContent}
        headerText={controls?.Download?.controlText?.replace('/', '/\n')}
        open={isModalOpen}
        onClose={onModalClose}
        shouldReturnFocusAfterClose={false}
        windowType={modalType}/>
      <div className="accordion accordion-flush row gy-4">
        <div className="accordion-it" ref={downloadPanelEl}>
          <h3 className="accordion-header" id="headingthree">
            <button className="nqt-dp--accordion-button accordion-button"
              ref={onFocusEl}
              onClick={
                (e) => !e.currentTarget.classList.contains('collapsed') ?
                  setAccExpanded(true) :
                  setAccExpanded(false)
              }
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#collapseThree"
              aria-expanded="true"
              aria-controls="collapseThree"
              id='nqt-shl--download-button'
              onKeyDown={tabToShareTopFromDownload(true)}>
              <span className="accordion-header--label">
                <div>
                  {
                    controls?.Download?.controlText?.substring(
                      0,
                      controls?.Download?.controlText?.indexOf('/') + 1
                    )
                  }
                </div>
                <div>
                  {
                    controls?.Download?.controlText?.substring(
                      controls?.Download?.controlText?.indexOf('/') + 1
                    )
                  }
                </div>
              </span>
              {
                accExpanded ?
                  <FontAwesomeIcon className='fa-plus' icon={faMinus}/> :
                  <FontAwesomeIcon className='fa-plus' icon={faPlus}/>
              }
            </button>
          </h3>
          <div id="collapseThree" className="accordion-collapse collapse" aria-labelledby="headingthree" data-bs-parent="#make-selections-accordion">
            <div className="accordion-body px-1 py-2">
              <div className="row row-cols-1 gy-3">
                {
                  !hideCheckboxes &&
                  <>
                    <div className="col">
                      <p className="dp-text" dangerouslySetInnerHTML={{__html: controls?.DwnldSelect ? controls?.DwnldSelect?.controlText : ''}}></p>
                    </div>
                    {/* test */}
                    <div className="col">
                      <button className="dp-acc--header"
                        onClick={() => setVarExpanded(!varExpanded)}>
                        <div className="nqt-icon-box">
                          {
                            varExpanded ?
                              <FontAwesomeIcon size='xs' icon={faMinus} className='fa-icon'/> :
                              <FontAwesomeIcon size='xs' icon={faPlus} className='fa-icon'/>
                          }
                        </div>
                        <h3 className="dp-acc--header-text">
                          {controls?.DwnldQDetail?.controlText}
                        </h3>
                      </button>
                      <div className={`dp-acc--header-children${varExpanded ? '' : ' d-none'} `}>
                        {/* Tab Checkboxes under question detail accordion */}
                        {
                          tabsForDownload.length > 0 &&
                          tabsForDownload.map((tab, ind)=> (
                            <li key={ind} >
                              <NQTCheckBox
                                id={'nqt-dp--tabs-' + ind}
                                containerClass="dp-checkbox-container"
                                checkBoxObj={tab}
                                label={tab.label}
                                type={TAB_TYPE}
                                updateHandler={
                                  (e: CHECKBOX_GROUP_TYPES) =>
                                    updateSelected(e, setIsValid, tabsForDownload, testsForDownload)
                                }/>
                            </li>
                          ))
                        }
                      </div>
                    </div>
                    <div className="col">
                      {/* Test Checkbox outside of question detail accordion */}
                      {
                        testsForDownload.length > 0 &&
                          testsForDownload.map((test, ind)=> (
                            <div key={ind}>
                              <div className='nqt-dp--divider'></div>
                              <li key={ind} className='nqt-dp--add-padding'>
                                <NQTCheckBox
                                  id={'nqt-dp--tests-' + ind}
                                  containerClass="dp-checkbox-container"
                                  checkBoxObj={test}
                                  label={test.label}
                                  type={TEST_TYPE}
                                  updateHandler={
                                    (e: CHECKBOX_GROUP_TYPES) =>
                                      updateSelected(e, setIsValid, tabsForDownload, testsForDownload)
                                  }/>
                              </li>
                            </div>
                          ))
                      }
                    </div>
                  </>
                }
                <div className="col">
                  {/* Available formats: HTML PDF WORD */}
                  <h3 className="dp-acc--header-text">{controls?.DwnldFormat?.controlText}</h3>
                  <fieldset className="nqt-dp--radio-buttons" role="radiogroup">
                    {
                      formats.length > 0 &&
                      formats.map((format, ind) => (

                        <div key={ind} className='nqt-dp--radio mt-1'>
                          <NQTRadioButton
                            checked={selectedFormat === format?.value}
                            id={'nqt-dp--radio-' + ind}
                            name='nqt-download-formats'
                            radioClassContainer='nqt-dp--radio-container w-100'
                            value={format.value.toString()}
                            label={format.label}
                            updateHandler={updateSelectedFormat}
                          />
                        </div>
                      ))
                    }
                  </fieldset>
                </div>

                <div className="nqt-dp--preview col">
                  { isDownloading && localDownloading && <Spinner size={20} containerClassName="nqt-dp--preview-spinner" />}
                  <button
                    disabled={!isValid || !!isDownloading}
                    onClick={() => onPreview()}
                    className='nqt-submit-button nqt-dp--preview-button'
                    type='submit'>{controls?.DwnldDownload?.controlText}</button>
                </div>
                {
                  isDownloading && localDownloading &&
                  <div className="nqt-dp--progress-container col" role="progressbar"
                    aria-valuenow={downloadProgress?.completed ?? 0}
                    aria-valuemin={0}
                    aria-valuemax={downloadProgress?.total ?? 0}>
                    <div className="nqt-dp--progress-label">
                      {
                        downloadProgress && downloadProgress.total > 0 ?
                          `Processing ${downloadProgress.completed} of ${downloadProgress.total} items` :
                          'Preparing download...'
                      }
                    </div>
                    <div className="nqt-dp--progress-bar-track">
                      <div
                        className="nqt-dp--progress-bar-fill"
                        style={{ width: `${downloadProgress && downloadProgress.total > 0 ? Math.min(100, Math.round((downloadProgress.completed / downloadProgress.total) * 100)) : 0}%` }}
                      ></div>
                    </div>
                  </div>
                }
                {
                  isDownloading && localDownloading &&
                  <div className="nqt-dp--cancel col">
                    <button
                      onClick={() => dispatch(loadDownloadCancel())}
                      className='nqt-cancel-button nqt-dp--cancel-button'
                      type='button'>Cancel</button>
                  </div>
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};


